import React from 'react';
import {
    Flex,
    Loader,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import * as S from './ShippingForm.style';
import DeliverySelect from './DeliverySelect';
import ClickAndCollectSelect from './ClickAndCollectSelect';
import { useFetcher, useLoaderData } from '@remix-run/react';
import type { ItemShippingType } from '@modules/ecommerce/context/ItemShippingContext';
import { useTracker } from '@archipro-website/tracker';
import SuburbSearchInput from '../suburb-search-input/SuburbSearchInput';
import type { Locality } from '../../type/locality';
import { useLatest } from 'ahooks';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import type { EcommerceUpdateShoppingCartAction } from '~/routes/remix-api.ecommerce.update-shopping-cart';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';

interface Props {
    shipping: ItemShippingType | null;
    onChange: (shipping: ItemShippingType) => void;
    isSample: boolean;
    onFetchStatusChange?: (fetching: boolean) => void;
}

const ShippingForm = ({
    shipping,
    onChange,
    isSample,
    onFetchStatusChange,
}: Props) => {
    const { keyframe } = useStyles();
    const { css } = useStyles({ rotateKeyframes: keyframe(S.RotateKeyframes) });
    const { product, shoppingCartSummary } =
        useLoaderData<ProductPageLoaderData>();
    const fetcher = useFetcher<EcommerceUpdateShoppingCartAction>();
    const onFetchStatusChangeRef = useLatest(onFetchStatusChange);

    const { shippingMethods } = useVariantShipping(isSample);
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();

    const onLocalityChange = (locality: Locality | null) => {
        if (!locality) return;

        tracker.log('DropDownSelectLocation', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: locality.Title,
            },
        });

        fetcher.submit(
            {
                locality: locality.ID.toString(),
            },
            {
                method: 'post',
                action: '/remix-api/ecommerce/update-shopping-cart',
            }
        );
    };
    const isLoading =
        fetcher.state !== 'idle' ||
        shippingMethods.some((m) => !m.ShippingCost);

    React.useEffect(() => {
        onFetchStatusChangeRef.current?.(isLoading);
    }, [isLoading, onFetchStatusChangeRef]);

    return (
        <div className={css(S.ShippingForm)}>
            <div className={css(S.Suburb)}>
                <SuburbSearchInput
                    locality={shoppingCartSummary.Locality}
                    onChange={onLocalityChange}
                />
            </div>
            <Flex className={css(S.MethodContainer)} column={mobile}>
                {isLoading ? (
                    <div className={css(S.Overlay)}>
                        <Loader />
                    </div>
                ) : (
                    <>
                        {shippingMethods.length > 0 && (
                            <DeliverySelect
                                shippingMethods={shippingMethods}
                                shipping={shipping}
                                onChange={onChange}
                            />
                        )}
                        {Boolean(
                            product.Professional?.PickupLocations.length
                        ) && (
                            <ClickAndCollectSelect
                                shipping={shipping}
                                onChange={onChange}
                            />
                        )}
                    </>
                )}
            </Flex>
        </div>
    );
};

export default ShippingForm;
